import React, { useCallback, useMemo, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { PenaltiesToggle } from '@features/osi/accruals/penaltiesToggle';
import { Box, Button } from '@mui/material';
import { ServiceTable } from '@entities/osi/accruals/serviceTable';
import { ServiceGroup } from '@entities/osi/accruals/serviceGroup';
import { useInjection } from 'inversify-react';
import { AddServiceButton } from '@features/osi/accruals/service/add';
import LoadingScreen from '@shared/components/LoadingScreen';
import { useTranslation } from '@shared/utils/i18n';
import { AddAdditionalServiceButton } from '@features/osi/accruals/additionalService/add';
import { AccuralDate } from '@features/osi/accruals/accuralsDate';
import { RemakeAccruals } from '@features/osi/accruals/remakeAccruals/RemakeAccruals';
// OSI-823: кнопка-подсказка «Как считается пеня»
import { PenaltiesHelpButton } from '@features/osi/accruals/penaltiesHelp';
import { queryClient } from '@shared/api/reactQuery';
import { type OsiService, OsiServiceIdentifier } from '@shared/types/mobx/services/osi';
import { getGetApiOsiIdV2ServicesQueryKey, useGetApiOsiIdV2Services } from '@/shared/api/orval/osi/osi';
import { ServiceGroupManagement } from '@features/osi/accruals/serviceGroupManagement/ui/ServiceGroupManagement';

// Фаза 0: определяем группу по code вместо хардкода ID
const isAdditionalServiceGroup = (groupCode: string | undefined | null) => groupCode === 'ADDITIONAL';

interface Props {
  hideSetDate?: boolean;
}

export const OsiAccrualsWidget: React.FC<Props> = observer(({ hideSetDate }) => {
  const osiModule = useInjection<OsiService>(OsiServiceIdentifier);
  const { isLoading, data } = useGetApiOsiIdV2Services(osiModule.osiId ?? 0);

  // Метод начислений в выпадающем списке должен идти в едином порядке для всех категорий
  // (как в категории TO). Бэк отдаёт accuralMethods в порядке строк allowed_accural_methods,
  // который различается между группами, поэтому сортируем по id (1→6) на фронте.
  const serviceGroups = useMemo(
    () =>
      (data?.result ?? []).map((group) => ({
        ...group,
        accuralMethods: [...(group.accuralMethods ?? [])].sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
      })),
    [data]
  );

  const isCanRemakeAccruals = osiModule.osiInfo?.canRemakeAccurals ?? false;

  const onReloadServiceGroups = async () => {
    const queryKey = getGetApiOsiIdV2ServicesQueryKey(osiModule.osiId ?? 0);
    await queryClient.invalidateQueries({ queryKey });
  };

  // Ref для функции смены счёта, получаемой из ServiceGroupManagement
  // Принимает serviceGroupId, опционально currentLinkedAccountId для подсветки текущего счёта, и groupCode для фильтрации.
  // Новые обязательные параметры: isCustom (произвольная группа = per-OSI, osi_id != null), groupNameRu/groupNameKz и reportCategoryId —
  // нужны для предзаполнения полей редактирования имени и категории дохода в попапе для произвольных групп.
  const editAccountFnRef = useRef<((serviceGroupId: number, currentLinkedAccountId: number | null | undefined, groupCode: string | undefined, isCustom: boolean, groupNameRu: string, groupNameKz: string, reportCategoryId: number | null) => void) | null>(null);

  const handleEditAccountReady = useCallback((fn: (serviceGroupId: number, currentLinkedAccountId: number | null | undefined, groupCode: string | undefined, isCustom: boolean, groupNameRu: string, groupNameKz: string, reportCategoryId: number | null) => void) => {
    editAccountFnRef.current = fn;
  }, []);

  const { fieldWithPrefix: fwp, t } = useTranslation();
  return (
    <Box>
      {hideSetDate ? null : <AccuralDate />}
      {/* OSI-823: справа от чекбокса пени — кнопка «Как считается пеня». Только во вкладке «Начисления»:
          в мастере регистрации виджет рендерится с hideSetDate — там кнопку не показываем */}
      <Box sx={{ display: 'flex', columnGap: '10px', alignItems: 'center' }}>
        <PenaltiesToggle />
        {hideSetDate ? null : <PenaltiesHelpButton />}
      </Box>
      {/* OSI-823: «Доначислить» перенесена под строку «Включить начисление пени», отдельной строкой по левому краю.
          Отступ от «Подключить группу» ниже даёт её собственный mt: 2 (ServiceGroupManagement); mb: 1 — небольшой запас */}
      {isCanRemakeAccruals && (
        <Box sx={{ display: 'flex', mb: 1 }}>
          <RemakeAccruals />
        </Box>
      )}

      {isLoading ? (
        <div
          style={{
            position: 'fixed',
            left: 0,
            top: 0,
            zIndex: 1001,
            width: '100vw',
            height: '100vh'
          }}
        >
          <LoadingScreen />
        </div>
      ) : (
        <>
          <ServiceGroupManagement osiId={osiModule.osiId ?? 0} reloadCb={onReloadServiceGroups} onEditAccountReady={handleEditAccountReady} />
          {serviceGroups.map((group) => (
            <ServiceGroup
              key={group.id}
              title={fwp(group, 'groupName')}
              printDetailsOnInvoice={group.printDetailOnInvoice ?? false}
              osiId={osiModule.osiId ?? 0}
              groupId={group.id ?? 0}
              RightTitleSlot={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto', flexShrink: 0 }}>
                  {/* Группа кнопок прижата к правому краю через ml:auto на обёртке; gap:1 — зазор между кнопками, чтобы они стояли рядом и не разъезжались при разной длине заголовка слева */}
                  {isAdditionalServiceGroup(group.code) ? (
                    <AddAdditionalServiceButton
                      serviceGroupId={group.id ?? 0}
                      reloadCb={onReloadServiceGroups}
                      defaultServiceNames={group.serviceNameExamples ?? []}
                    />
                  ) : (
                    <AddServiceButton
                      serviceGroupId={group.id ?? 0}
                      serviceGroupCode={group.code}
                      // OSI-786: флаг произвольной группы с бэка (v2/services) — гейтинг чекбокса requiresMonthlyConfirmation
                      isCustom={group.isCustom ?? false}
                      accrualMethods={group.accuralMethods ?? []}
                      defaultServiceNames={group.serviceNameExamples ?? []}
                      reloadCb={onReloadServiceGroups}
                    />
                  )}

                  {/* Номер счёта — flexShrink:0, чтобы длинный номер (IBAN) не сжимался; прижатие вправо теперь делает обёрточный Box */}
                  {/* OSI-786: редактирование имени/категории группы в попапе доступно только для произвольных групп
                      (isCustom = per-OSI, osi_id != null: мигрированные «Коммунальные/Котельная/Лифт» + CUSTOM_).
                      Гейт на isCustom, а не на canChangeName — у системных групп can_change_name=true, но менять их имя нельзя. */}
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => editAccountFnRef.current?.(group.id ?? 0, (group as any).linkedAccountId, group.code, group.isCustom ?? false, group.groupNameRu ?? '', group.groupNameKz ?? '', (group as any).reportCategoryId ?? null)}
                    sx={{ flexShrink: 0 }}
                  >
                    {(group as any).linkedAccountNumber || t('accruals:linkAccount')}
                  </Button>
                </Box>
              }
            >
              <ServiceTable group={group} reloadCb={onReloadServiceGroups} />
            </ServiceGroup>
          ))}
        </>
      )}
    </Box>
  );
});
